// pages/Dashboard.js - Updated with Low Stock Alert Modal

import "../styles/main.css";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { database } from "../firebase/firebase";
import { ref, onValue } from "firebase/database";

export default function Dashboard() {

  const navigate = useNavigate();

  /* ================= STATES ================= */
  const [companyName, setCompanyName] = useState("Jauhari Billing");
  const [companyDetails, setCompanyDetails] = useState({});
  
  const [todaySales, setTodaySales] = useState(0);
  const [todayProfit, setTodayProfit] = useState(0);
  const [pendingAmount, setPendingAmount] = useState(0);
  const [lowStockCount, setLowStockCount] = useState(0);
  
  const [totalSales, setTotalSales] = useState(0);
  const [totalPurchases, setTotalPurchases] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalInvoices, setTotalInvoices] = useState(0);
  
  const [lowStockItems, setLowStockItems] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  
  const [notificationCount, setNotificationCount] = useState(0);
  const [showLowStockModal, setShowLowStockModal] = useState(false);
  
  const [financialYear, setFinancialYear] = useState({
    start: "01-04-2025",
    end: "31-03-2026",
    display: "2025-26"
  });

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    // Load company details
    const companyRef = ref(database, "company");
    onValue(companyRef, (snapshot) => {
      const data = snapshot.val();
      if (data && data.name) {
        setCompanyName(data.name);
        setCompanyDetails(data);
      }
    });

    // Load settings for financial year
    const settingsRef = ref(database, "settings");
    onValue(settingsRef, (snapshot) => {
      const data = snapshot.val();
      if (data && data.financialYear) {
        const year = data.financialYear;
        const startYear = year.split('-')[0];
        const endYear = year.split('-')[1];
        setFinancialYear({
          start: `01-04-${startYear}`,
          end: `31-03-20${endYear}`,
          display: year
        });
      }
    });

    // Load financial years data for sales and purchases
    const financialYearsRef = ref(database, "financialYears");
    onValue(financialYearsRef, (snapshot) => {
      const yearsData = snapshot.val();
      if (yearsData) {
        let totalSalesAmount = 0;
        let totalPurchaseAmount = 0;
        let invoiceCount = 0;
        
        Object.values(yearsData).forEach(yearData => {
          if (yearData.bills) {
            Object.values(yearData.bills).forEach(bill => {
              totalSalesAmount += bill.grandTotal || 0;
              invoiceCount++;
            });
          }
          if (yearData.purchases) {
            Object.values(yearData.purchases).forEach(purchase => {
              totalPurchaseAmount += purchase.grandTotal || 0;
            });
          }
        });
        
        setTotalSales(totalSalesAmount);
        setTotalPurchases(totalPurchaseAmount);
        setTotalInvoices(invoiceCount);
        
        // Calculate today's sales
        const today = new Date().toISOString().split('T')[0];
        let todayTotal = 0;
        Object.values(yearsData).forEach(yearData => {
          if (yearData.bills) {
            Object.values(yearData.bills).forEach(bill => {
              if (bill.date === today) {
                todayTotal += bill.grandTotal || 0;
              }
            });
          }
        });
        setTodaySales(todayTotal);
        setTodayProfit(todayTotal * 0.2);
      }
    });

    // Load customers data for pending payments
    const customersRef = ref(database, "customers");
    onValue(customersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        let customerCount = 0;
        let pending = 0;
        Object.values(data).forEach((customer) => {
          customerCount++;
          pending += Math.abs(customer.balance || 0);
        });
        setTotalCustomers(customerCount);
        setPendingAmount(pending);
      }
    });

    // Load items data for stock - CHECK FOR STOCK BELOW 10
    const itemsRef = ref(database, "items");
    onValue(itemsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        let lowStock = 0;
        let total = 0;
        const lowItems = [];
        
        Object.entries(data).forEach(([id, item]) => {
          total++;
          const stock = parseInt(item.currentStock) || 0;
          
          // Alert when stock goes below 10
          if (stock < 10) {
            lowStock++;
            lowItems.push({
              id: id,
              name: item.name,
              stock: stock,
              unit: item.unit || "NOS",
              minStock: item.minimumStock || 5,
              status: stock === 0 ? "Out of Stock" : "Low Stock"
            });
          }
        });
        
        // Sort by stock (lowest first)
        lowItems.sort((a, b) => a.stock - b.stock);
        
        setLowStockCount(lowStock);
        setTotalItems(total);
        setLowStockItems(lowItems);
        setNotificationCount(lowStock);
      }
    });

    // Load top products (for now using dummy data)
    setTopProducts([
      { name: "Product 1", sales: 45 },
      { name: "Product 2", sales: 32 },
      { name: "Product 3", sales: 28 }
    ]);

  }, []);

  /* ================= HANDLERS ================= */
  const handleNotificationClick = () => {
    setShowLowStockModal(true);
  };

  const closeModal = () => {
    setShowLowStockModal(false);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Get stock status color
  const getStockStatusColor = (stock) => {
    if (stock === 0) return "#dc3545";
    if (stock < 5) return "#ff9800";
    return "#ffc107";
  };

  return (
    <div className="dashboard-container">
      {/* ================= HEADER ================= */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="company-info">
            <h1 className="company-name">{companyName}</h1>
            {companyDetails.gstin && (
              <p className="company-gst">GST: {companyDetails.gstin}</p>
            )}
          </div>
          <div className="header-actions">
            <button className="icon-btn" onClick={handleNotificationClick}>
              🔔
              {notificationCount > 0 && (
                <span className="notification-badge">{notificationCount}</span>
              )}
            </button>
            <button className="icon-btn" onClick={() => navigate("/settings")}>
              ⚙️
            </button>
          </div>
        </div>
        <div className="financial-year">
          📅 FY: {financialYear.start} → {financialYear.end} ({financialYear.display})
        </div>
      </div>

      {/* ================= STATS CARDS ================= */}
      <div className="stats-grid">
        <div className="stat-card" style={{ background: "linear-gradient(135deg, #667eea, #764ba2)" }} onClick={() => navigate("/daily-sales")}>
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <p className="stat-label">Today Sales</p>
            <h3 className="stat-value">₹ {todaySales.toFixed(2)}</h3>
          </div>
        </div>

        <div className="stat-card" style={{ background: "linear-gradient(135deg, #f093fb, #f5576c)" }}>
          <div className="stat-icon">📈</div>
          <div className="stat-content">
            <p className="stat-label">Est. Profit</p>
            <h3 className="stat-value">₹ {todayProfit.toFixed(2)}</h3>
          </div>
        </div>

        <div className="stat-card" style={{ background: "linear-gradient(135deg, #4facfe, #00f2fe)" }} onClick={() => navigate("/payments")}>
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <p className="stat-label">Pending</p>
            <h3 className="stat-value">₹ {pendingAmount.toFixed(2)}</h3>
          </div>
        </div>

        <div className="stat-card" style={{ background: lowStockCount > 0 ? "linear-gradient(135deg, #ff0844, #ffb199)" : "linear-gradient(135deg, #43e97b, #38f9d7)" }} onClick={() => navigate("/stock")}>
          <div className="stat-icon">📦</div>
          <div className="stat-content">
            <p className="stat-label">Low Stock</p>
            <h3 className="stat-value">{lowStockCount}</h3>
          </div>
        </div>
      </div>

      {/* ================= QUICK STATS ================= */}
      <div className="quick-stats">
        <div className="quick-stat">
          <span className="stat-number">{totalItems}</span>
          <span className="stat-name">Items</span>
        </div>
        <div className="quick-stat">
          <span className="stat-number">{totalCustomers}</span>
          <span className="stat-name">Customers</span>
        </div>
        <div className="quick-stat">
          <span className="stat-number">{totalInvoices}</span>
          <span className="stat-name">Invoices</span>
        </div>
        <div className="quick-stat">
          <span className="stat-number">₹{(totalSales/1000).toFixed(0)}k</span>
          <span className="stat-name">Sales</span>
        </div>
      </div>

      {/* ================= WELCOME BANNER ================= */}
      <div className="welcome-banner">
        <div>
          <h3>👋 Welcome back!</h3>
          <p>{new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
      </div>

      {/* ================= QUICK ACTIONS ================= */}
      <div className="section">
        <h2 className="section-title">⚡ Quick Actions</h2>
        <div className="actions-grid">
          <div className="action-card" onClick={() => navigate("/invoice/new")}>
            <div className="action-icon">🧾</div>
            <span>Tax Invoice</span>
          </div>
          <div className="action-card" onClick={() => navigate("/raw-bill/new")}>
            <div className="action-icon">📄</div>
            <span>Cash Bill</span>
          </div>
          <div className="action-card" onClick={() => navigate("/purchase")}>
            <div className="action-icon">📦</div>
            <span>Purchase</span>
          </div>
          <div className="action-card" onClick={() => navigate("/payment/new")}>
            <div className="action-icon">💰</div>
            <span>Payment</span>
          </div>
        </div>
      </div>

      {/* ================= BILLING ================= */}
      <div className="section">
        <h2 className="section-title">🧾 Billing</h2>
        <div className="menu-grid">
          <div className="menu-card" onClick={() => navigate("/invoice/new")}>
            <div className="menu-icon">🧾</div>
            <div className="menu-text">Tax Invoice</div>
          </div>
          <div className="menu-card" onClick={() => navigate("/raw-bill/new")}>
            <div className="menu-icon">📝</div>
            <div className="menu-text">Cash Bill</div>
          </div>
          <div className="menu-card" onClick={() => navigate("/invoicelist")}>
            <div className="menu-icon">📋</div>
            <div className="menu-text">Invoice List</div>
          </div>
          <div className="menu-card" onClick={() => navigate("/raw-bills")}>
            <div className="menu-icon">📋</div>
            <div className="menu-text">Cash Bill List</div>
          </div>
        </div>
      </div>

      {/* ================= INVENTORY ================= */}
      <div className="section">
        <h2 className="section-title">📦 Inventory</h2>
        <div className="menu-grid">
          <div className="menu-card" onClick={() => navigate("/items")}>
            <div className="menu-icon">📦</div>
            <div className="menu-text">Products</div>
          </div>
          <div className="menu-card" onClick={() => navigate("/stock")}>
            <div className="menu-icon">📊</div>
            <div className="menu-text">Stock</div>
          </div>
        </div>
      </div>

      {/* ================= PURCHASE ================= */}
      <div className="section">
        <h2 className="section-title">🛒 Purchase</h2>
        <div className="menu-grid">
          <div className="menu-card" onClick={() => navigate("/purchase")}>
            <div className="menu-icon">➕</div>
            <div className="menu-text">New Purchase</div>
          </div>
          <div className="menu-card" onClick={() => navigate("/PurchaseList")}>
            <div className="menu-icon">📋</div>
            <div className="menu-text">Purchase List</div>
          </div>
        </div>
      </div>

      {/* ================= CUSTOMERS & PAYMENTS ================= */}
      <div className="two-col-grid">
        <div className="section">
          <h2 className="section-title">👥 Customers</h2>
          <div className="menu-grid vertical">
            <div className="menu-card" onClick={() => navigate("/customers")}>
              <div className="menu-icon">👤</div>
              <div className="menu-text">All Customers</div>
            </div>
            <div className="menu-card" onClick={() => navigate("/ledger")}>
              <div className="menu-icon">📒</div>
              <div className="menu-text">Ledger</div>
            </div>
          </div>
        </div>

        <div className="section">
          <h2 className="section-title">💸 Payments</h2>
          <div className="menu-grid vertical">
            <div className="menu-card" onClick={() => navigate("/payment/new")}>
              <div className="menu-icon">➕</div>
              <div className="menu-text">New Payment</div>
            </div>
            <div className="menu-card" onClick={() => navigate("/payments")}>
              <div className="menu-icon">📋</div>
              <div className="menu-text">Payment List</div>
            </div>
          </div>
        </div>
      </div>

      {/* ================= ACCOUNTING & REPORTS ================= */}
      <div className="two-col-grid">
        <div className="section">
          <h2 className="section-title">💰 Accounting</h2>
          <div className="menu-grid vertical">
            <div className="menu-card" onClick={() => navigate("/day-book")}>
              <div className="menu-icon">📘</div>
              <div className="menu-text">Day Book</div>
            </div>
            <div className="menu-card" onClick={() => navigate("/ledger")}>
              <div className="menu-icon">📒</div>
              <div className="menu-text">Ledger</div>
            </div>
            <div className="menu-card" onClick={() => navigate("/reports")}>
              <div className="menu-icon">📈</div>
              <div className="menu-text">Reports</div>
            </div>
          </div>
        </div>

        <div className="section">
          <h2 className="section-title">📊 Reports</h2>
          <div className="menu-grid vertical">
            <div className="menu-card" onClick={() => navigate("/daily-sales")}>
              <div className="menu-icon">📊</div>
              <div className="menu-text">Daily Sales</div>
            </div>
            <div className="menu-card" onClick={() => navigate("/stock-report")}>
              <div className="menu-icon">📦</div>
              <div className="menu-text">Stock Report</div>
            </div>
            <div className="menu-card" onClick={() => navigate("/day-book")}>
              <div className="menu-icon">📘</div>
              <div className="menu-text">Day Book</div>
            </div>
          </div>
        </div>
      </div>

      {/* ================= DATA MANAGEMENT ================= */}
      <div className="section">
        <h2 className="section-title">💾 Data Management</h2>
        <div className="menu-grid">
          <div className="menu-card" onClick={() => navigate("/data-management")}>
            <div className="menu-icon">☁️</div>
            <div className="menu-text">Backup & Restore</div>
          </div>
          <div className="menu-card" onClick={() => navigate("/data-management")}>
            <div className="menu-icon">📤</div>
            <div className="menu-text">CA Export</div>
          </div>
          <div className="menu-card" onClick={() => navigate("/settings")}>
            <div className="menu-icon">⚙️</div>
            <div className="menu-text">Settings</div>
          </div>
        </div>
      </div>

      

      {/* ================= LOW STOCK ALERT MODAL ================= */}
      {showLowStockModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>⚠️ Low Stock Alert</h2>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            <div className="modal-body">
              {lowStockItems.length === 0 ? (
                <div className="no-alerts">
                  <div className="no-alerts-icon">✅</div>
                  <p>No low stock items found</p>
                  <p className="no-alerts-sub">All items have sufficient stock</p>
                </div>
              ) : (
                <>
                  <p className="alert-summary">
                    {lowStockItems.length} item(s) are running low on stock (below 10 units)
                  </p>
                  <div className="stock-alert-list">
                    {lowStockItems.map((item, index) => (
                      <div key={index} className="stock-alert-item">
                        <div className="alert-item-info">
                          <div className="alert-item-name">{item.name}</div>
                          <div className="alert-item-details">
                            <span className="alert-unit">Unit: {item.unit}</span>
                            {item.minStock > 0 && (
                              <span className="alert-min-stock">Min: {item.minStock}</span>
                            )}
                          </div>
                        </div>
                        <div className="alert-item-stock">
                          <span 
                            className="stock-number" 
                            style={{ color: getStockStatusColor(item.stock) }}
                          >
                            {item.stock}
                          </span>
                          <span className="stock-unit">{item.unit}</span>
                          <span className={`stock-status ${item.stock === 0 ? 'out' : 'low'}`}>
                            {item.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="modal-actions">
                    <button className="btn-primary" onClick={() => {
                      closeModal();
                      navigate("/stock");
                    }}>
                      View All Stock
                    </button>
                    <button className="btn-secondary" onClick={() => {
                      closeModal();
                      navigate("/purchase");
                    }}>
                      Add Purchase
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ================= BOTTOM NAV ================= */}
      <div className="bottom-nav">
        <div onClick={() => navigate("/")} className="nav-item active">
          🏠
          <p>Home</p>
        </div>
        <div onClick={() => navigate("/items")} className="nav-item">
          📦
          <p>Items</p>
        </div>
        <div onClick={() => navigate("/customers")} className="nav-item">
          👥
          <p>Masters</p>
        </div>
        <div onClick={() => navigate("/invoicelist")} className="nav-item">
          🧾
          <p>Invoices</p>
        </div>
      </div>

      <style>{`
        .dashboard-container {
          min-height: 100vh;
          background: #f5f5f5;
          padding-bottom: 70px;
        }

        /* Header */
        .dashboard-header {
          background: linear-gradient(135deg, #5a60ea, #6f5bff);
          color: white;
          padding: 20px;
          position: sticky;
          top: 0;
          z-index: 100;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 15px;
        }

        .company-name {
          font-size: 1.5rem;
          margin: 0;
          font-weight: 600;
        }

        .company-gst {
          font-size: 0.7rem;
          opacity: 0.9;
          margin: 4px 0 0;
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .icon-btn {
          background: rgba(255,255,255,0.2);
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          font-size: 1.2rem;
          cursor: pointer;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .icon-btn:hover {
          background: rgba(255,255,255,0.3);
          transform: scale(1.05);
        }

        .notification-badge {
          position: absolute;
          top: -5px;
          right: -5px;
          background: red;
          color: white;
          font-size: 0.7rem;
          padding: 2px 6px;
          border-radius: 10px;
          min-width: 18px;
          text-align: center;
        }

        .financial-year {
          font-size: 0.75rem;
          margin-top: 10px;
          opacity: 0.9;
          text-align: center;
        }

        /* Stats Grid */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 15px;
          padding: 20px;
        }

        .stat-card {
          background: white;
          border-radius: 16px;
          padding: 20px;
          display: flex;
          align-items: center;
          gap: 15px;
          cursor: pointer;
          transition: transform 0.2s, box-shadow 0.2s;
          color: white;
        }

        .stat-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.15);
        }

        .stat-icon {
          font-size: 2rem;
        }

        .stat-content {
          flex: 1;
        }

        .stat-label {
          font-size: 0.7rem;
          opacity: 0.9;
          margin: 0;
        }

        .stat-value {
          font-size: 1.3rem;
          margin: 5px 0 0;
          font-weight: bold;
        }

        /* Quick Stats */
        .quick-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 10px;
          padding: 0 20px 20px;
        }

        .quick-stat {
          background: white;
          padding: 12px;
          border-radius: 12px;
          text-align: center;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }

        .stat-number {
          display: block;
          font-size: 1.2rem;
          font-weight: bold;
          color: #5a60ea;
        }

        .stat-name {
          font-size: 0.7rem;
          color: #666;
        }

        /* Welcome Banner */
        .welcome-banner {
          background: linear-gradient(135deg, #5a60ea20, #6f5bff20);
          margin: 0 20px 20px;
          padding: 15px 20px;
          border-radius: 16px;
          border: 1px solid #5a60ea30;
        }

        .welcome-banner h3 {
          margin: 0 0 5px;
          color: #5a60ea;
          font-size: 1.1rem;
        }

        .welcome-banner p {
          margin: 0;
          font-size: 0.75rem;
          color: #666;
        }

        /* Sections */
        .section {
          margin: 0 20px 20px;
        }

        .section-title {
          font-size: 1rem;
          margin-bottom: 12px;
          color: #333;
          font-weight: 600;
        }

        /* Grid Layouts */
        .actions-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
          gap: 12px;
        }

        .menu-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
          gap: 12px;
        }

        .menu-grid.vertical {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .two-col-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
          margin: 0 20px 20px;
        }

        /* Cards */
        .action-card, .menu-card {
          background: white;
          border-radius: 12px;
          padding: 12px;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s;
          position: relative;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }

        .action-card:hover, .menu-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          background: #f8f9ff;
        }

        .action-icon {
          font-size: 1.8rem;
          margin-bottom: 5px;
        }

        .menu-icon {
          font-size: 1.5rem;
          margin-bottom: 5px;
        }

        .menu-text {
          font-size: 0.75rem;
          font-weight: 500;
        }

        .badge {
          position: absolute;
          top: 5px;
          right: 5px;
          background: red;
          color: white;
          font-size: 0.7rem;
          padding: 2px 6px;
          border-radius: 10px;
        }

        .badge.warning {
          background: #ff9800;
        }

        /* Top Products */
        .top-products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
          gap: 12px;
        }

        .product-card {
          background: white;
          border-radius: 12px;
          padding: 12px;
          text-align: center;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }

        .product-icon {
          font-size: 1.5rem;
          margin-bottom: 5px;
        }

        .product-name {
          font-size: 0.7rem;
          font-weight: 500;
          margin-bottom: 3px;
        }

        .product-sales {
          font-size: 0.6rem;
          color: #666;
        }

        .empty-state {
          text-align: center;
          padding: 20px;
          color: #999;
          background: white;
          border-radius: 12px;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: fadeIn 0.2s ease;
        }

        .modal-container {
          background: white;
          border-radius: 16px;
          width: 90%;
          max-width: 500px;
          max-height: 80vh;
          overflow: hidden;
          animation: slideUp 0.3s ease;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #eee;
          background: linear-gradient(135deg, #fff5f5, #fff);
        }

        .modal-header h2 {
          margin: 0;
          font-size: 1.3rem;
          color: #ff4444;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #999;
          transition: color 0.2s;
        }

        .modal-close:hover {
          color: #333;
        }

        .modal-body {
          padding: 20px;
          max-height: 60vh;
          overflow-y: auto;
        }

        .alert-summary {
          background: #fff3e0;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 15px;
          font-size: 0.85rem;
          color: #856404;
          border-left: 3px solid #ff9800;
        }

        .stock-alert-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 20px;
        }

        .stock-alert-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px;
          background: #f8f9fa;
          border-radius: 10px;
          border-left: 3px solid #ff9800;
        }

        .alert-item-info {
          flex: 1;
        }

        .alert-item-name {
          font-weight: bold;
          font-size: 0.9rem;
          margin-bottom: 4px;
        }

        .alert-item-details {
          display: flex;
          gap: 10px;
          font-size: 0.7rem;
          color: #666;
        }

        .alert-item-stock {
          text-align: right;
        }

        .stock-number {
          font-size: 1.2rem;
          font-weight: bold;
        }

        .stock-unit {
          font-size: 0.7rem;
          color: #666;
          margin-left: 2px;
        }

        .stock-status {
          display: block;
          font-size: 0.65rem;
          padding: 2px 6px;
          border-radius: 10px;
          margin-top: 4px;
        }

        .stock-status.low {
          background: #fff3e0;
          color: #ff9800;
        }

        .stock-status.out {
          background: #ffebee;
          color: #dc3545;
        }

        .modal-actions {
          display: flex;
          gap: 10px;
          margin-top: 15px;
        }

        .btn-primary, .btn-secondary {
          flex: 1;
          padding: 10px;
          border: none;
          border-radius: 8px;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-primary {
          background: #5a60ea;
          color: white;
        }

        .btn-primary:hover {
          background: #4a50d8;
        }

        .btn-secondary {
          background: #f0f0f0;
          color: #333;
        }

        .btn-secondary:hover {
          background: #e0e0e0;
        }

        .no-alerts {
          text-align: center;
          padding: 30px 20px;
        }

        .no-alerts-icon {
          font-size: 3rem;
          margin-bottom: 15px;
        }

        .no-alerts p {
          margin: 5px 0;
          color: #666;
        }

        .no-alerts-sub {
          font-size: 0.75rem;
          color: #999;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from {
            transform: translateY(50px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        /* Bottom Navigation */
        .bottom-nav {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: white;
          display: flex;
          justify-content: space-around;
          padding: 8px 0;
          box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
          z-index: 100;
        }

        .nav-item {
          text-align: center;
          cursor: pointer;
          color: #999;
          font-size: 1.2rem;
          transition: color 0.2s;
          padding: 5px 0;
        }

        .nav-item.active {
          color: #5a60ea;
        }

        .nav-item p {
          margin: 2px 0 0;
          font-size: 0.65rem;
        }

        /* Desktop Styles */
        @media (min-width: 768px) {
          .dashboard-header {
            padding: 20px 40px;
          }
          
          .company-name {
            font-size: 1.8rem;
          }
          
          .stats-grid {
            padding: 20px 40px;
            gap: 20px;
          }
          
          .quick-stats {
            padding: 0 40px 20px;
          }
          
          .section {
            margin: 0 40px 20px;
          }
          
          .two-col-grid {
            margin: 0 40px 20px;
          }
          
          .actions-grid {
            grid-template-columns: repeat(4, 1fr);
          }
          
          .menu-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }

        /* Tablet Styles */
        @media (min-width: 1024px) {
          .dashboard-container {
            max-width: 1200px;
            margin: 0 auto;
          }
          
          .stats-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }

        /* Mobile Styles */
        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
            padding: 15px;
          }
          
          .quick-stats {
            gap: 8px;
          }
          
          .stat-card {
            padding: 15px;
          }
          
          .stat-icon {
            font-size: 1.5rem;
          }
          
          .stat-value {
            font-size: 1rem;
          }
          
          .actions-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .menu-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .two-col-grid {
            grid-template-columns: 1fr;
            gap: 15px;
          }
        }
      `}</style>
    </div>
  );
}